import { useState } from 'react';
import type { ResumeComparison, ResumeVersion } from '../../lib/resume/types';
import { compareVersions } from '../../lib/resume/versions';
import { Badge, Button, Card, SectionHeader, TextField, controlClass } from '../ui';
import { CompareIcon, RestoreIcon } from '../ui/icons';

interface Props {
  versions: ResumeVersion[];
  onSave: (name: string) => void;
  onRestore: (versionId: string) => void;
}

export function VersionPanel({ versions, onSave, onRestore }: Props) {
  const [name, setName] = useState('');
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [comparison, setComparison] = useState<ResumeComparison | null>(null);
  const [status, setStatus] = useState('');

  const save = () => {
    const trimmed = name.trim();
    const label = trimmed.length > 0 ? trimmed : `Version ${versions.length + 1}`;
    onSave(label);
    setName('');
    setComparison(null);
    setStatus(`Saved “${label}”.`);
  };

  const compare = () => {
    const from = versions.find((version) => version.id === fromId);
    const to = versions.find((version) => version.id === toId);
    if (!from || !to) {
      setStatus('Choose two versions to compare.');
      setComparison(null);
      return;
    }
    setComparison(compareVersions(from, to));
    setStatus('');
  };

  const selectClass = controlClass;

  return (
    <Card aria-labelledby="versions-heading">
      <SectionHeader
        id="versions-heading"
        title="Versions"
        description="Save named snapshots of this draft, compare them, and restore one. Versions are held in memory only and are lost when the page is refreshed."
        action={<Badge tone="navy">{versions.length} saved</Badge>}
      />

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="w-full sm:w-56">
          <TextField
            label="Version name"
            value={name}
            onChange={setName}
            placeholder="e.g. Frontend focus"
          />
        </div>
        <Button variant="primary" onClick={save}>
          Save version
        </Button>
        {status ? (
          <span role="status" className="text-sm text-ink-soft">
            {status}
          </span>
        ) : null}
      </div>

      {versions.length === 0 ? (
        <p className="mt-4 rounded-md border border-dashed border-line-strong bg-surface-sunken px-4 py-6 text-center text-sm text-ink-faint">
          No saved versions yet.
        </p>
      ) : (
        <ul className="mt-4 space-y-2" aria-label="Saved versions">
          {versions.map((version) => (
            <li
              key={version.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-surface-sunken px-3 py-2"
            >
              <span className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
                <span className="font-semibold text-ink">{version.name}</span>
                <Badge tone="neutral">{version.template}</Badge>
                <span className="text-xs text-ink-faint">
                  {new Date(version.savedAt).toLocaleString()}
                </span>
              </span>
              <Button
                size="sm"
                variant="quiet"
                icon={<RestoreIcon />}
                onClick={() => onRestore(version.id)}
              >
                Restore into draft
              </Button>
            </li>
          ))}
        </ul>
      )}

      {versions.length >= 2 ? (
        <div className="mt-5 border-t border-line pt-4">
          <h3 className="text-sm font-semibold text-ink">Compare versions</h3>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="flex min-w-40 flex-1 flex-col gap-1 text-sm">
              <span className="font-medium text-ink-soft">From</span>
              <select
                aria-label="Compare from version"
                value={fromId}
                onChange={(e) => setFromId(e.target.value)}
                className={selectClass}
              >
                <option value="">Select…</option>
                {versions.map((version) => (
                  <option key={version.id} value={version.id}>
                    {version.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex min-w-40 flex-1 flex-col gap-1 text-sm">
              <span className="font-medium text-ink-soft">To</span>
              <select
                aria-label="Compare to version"
                value={toId}
                onChange={(e) => setToId(e.target.value)}
                className={selectClass}
              >
                <option value="">Select…</option>
                {versions.map((version) => (
                  <option key={version.id} value={version.id}>
                    {version.name}
                  </option>
                ))}
              </select>
            </label>
            <Button variant="secondary" icon={<CompareIcon />} onClick={compare}>
              Compare
            </Button>
          </div>

          {comparison ? (
            <div className="mt-3" aria-live="polite">
              {comparison.identical ? (
                <p className="text-sm text-ink-soft">These versions are identical.</p>
              ) : (
                <ul className="space-y-1.5" aria-label="Version differences">
                  {comparison.lines.map((line, index) => (
                    <li
                      key={index}
                      className="flex flex-wrap items-center gap-1.5 rounded-md border border-line bg-surface-sunken px-3 py-2 text-sm"
                    >
                      <Badge
                        tone={
                          line.kind === 'added'
                            ? 'verified'
                            : line.kind === 'removed'
                              ? 'danger'
                              : 'gap'
                        }
                      >
                        {line.kind}
                      </Badge>
                      <span className="font-medium text-ink-soft">{line.label}:</span>
                      {line.kind !== 'added' && line.before ? (
                        <span className="text-danger line-through">{line.before}</span>
                      ) : null}
                      {line.kind === 'changed' ? <span className="text-ink-faint">→</span> : null}
                      {line.kind !== 'removed' && line.after ? (
                        <span className="font-medium text-verified">{line.after}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}
